import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../hooks/useCart';
import { useCreateOrder } from '../hooks/useOrder';
import { useNotification } from '../hooks/useNotification';
import { useAuth } from '../hooks/useAuth';

import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { ShieldCheck, ArrowLeft, Loader2, Banknote } from 'lucide-react';
import { CreateOrderRequest, Address } from '../types';
import { userService } from '../services/user.service';
import { checkoutService, CalculateResponse } from '../services/checkout.service';
import { env } from '../config/env';

const calculateShipping = (subtotal: number): number => {
  if (subtotal === 0) return 0;
  if (subtotal >= env.FREE_SHIPPING_THRESHOLD) return 0;
  return env.FLAT_SHIPPING_COST;
};

export const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { items: cartItems, subtotal, clearCart } = useCart();
  const { showSuccess, showError } = useNotification();
  const { mutateAsync, isLoading } = useCreateOrder();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
  });

  const [codSelected, setCodSelected] = useState(false);
  const [isAddressesLoading, setIsAddressesLoading] = useState(false);
  const [calculation, setCalculation] = useState<CalculateResponse | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationFailed, setCalculationFailed] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});

  // Calculate totals using API when cart changes
  useEffect(() => {
    const calculateTotals = async () => {
      if (cartItems.length === 0) {
        setCalculation(null);
        return;
      }

      setIsCalculating(true);
      setCalculationFailed(false);
      try {
        const request = {
          items: cartItems.map(item => ({
            product_id: item.productId,
            quantity: item.quantity
          }))
        };
        const response = await checkoutService.calculate(request);
        setCalculation(response);
      } catch (error) {
        console.error('Failed to calculate totals:', error);
        setCalculationFailed(true);
        setCalculation(null);
      } finally {
        setIsCalculating(false);
      }
    };

    calculateTotals();
  }, [cartItems, subtotal]);

  const shipping = calculation?.shipping_amount ?? calculateShipping(subtotal);
  const tax = calculation?.tax_amount ?? 0;
  const discount = calculation?.discount_amount ?? 0;
  const total = calculation?.total_amount ?? (subtotal + shipping + tax - discount);

  // Load default address and customer profile if available
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.sub) return;

      setIsAddressesLoading(true);
      try {
        const [addresses, customerData] = await Promise.all([
          userService.getAddresses(user.sub).catch(() => []),
          userService.getCustomer(user.sub).catch(() => null)
        ]);

        const defaultShipping = addresses.find(a => a.type === 'shipping' && a.is_default) ||
          addresses.find(a => a.type === 'shipping');

        const customerProfile = customerData;

        let formDataUpdates: Partial<typeof formData> = {};

        if (defaultShipping) {
          formDataUpdates = {
            name: defaultShipping.name || user.name || '',
            phone: defaultShipping.phone || '',
            line1: defaultShipping.line1,
            line2: defaultShipping.line2 || '',
            city: defaultShipping.city,
            state: defaultShipping.state || '',
            postal_code: defaultShipping.postal_code || '',
            country: defaultShipping.country,
          };
          showSuccess('Default address loaded');
        }

        // Always prioritize the customer profile's phone number if it exists
        if (customerProfile?.phone) {
          formDataUpdates.phone = customerProfile.phone;
        }

        if (Object.keys(formDataUpdates).length > 0) {
          setFormData(prev => ({ ...prev, ...formDataUpdates }));
        }

      } catch (error) {
        console.error('Failed to load user data', error);
      } finally {
        setIsAddressesLoading(false);
      }
    };

    loadUserData();
  }, [user?.sub, user?.name, showSuccess]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'country' ? value.toUpperCase() : value }));
    // Clear error for this field when user starts typing
    if (value.trim()) {
      setFormErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleConfirmOrder = async () => {
    // Validate authentication
    if (!user?.sub) {
      console.error('User not authenticated');
      showError('Please login to continue with your order');
      navigate('/login', { state: { returnTo: '/checkout' } });
      return;
    }

    // Validate form
    const missingFields = [];
    if (!formData.name) missingFields.push('name');
    if (!formData.phone) missingFields.push('phone');
    if (!formData.line1) missingFields.push('line1');
    if (!formData.city) missingFields.push('city');
    if (!formData.state) missingFields.push('state');
    if (!formData.postal_code) missingFields.push('postal_code');
    if (!formData.country) missingFields.push('country');

    if (missingFields.length > 0) {
      console.error('Missing required fields:', missingFields);
      
      // Mark missing fields as errors
      const newErrors: Record<string, boolean> = {};
      missingFields.forEach(field => {
        newErrors[field] = true;
      });
      setFormErrors(newErrors);
      
      showError(`Missing required fields: ${missingFields.join(', ')}. Please fill in all shipping information.`);
      return;
    }

    if (cartItems.length === 0) {
      console.error('Cart is empty');
      showError('Your cart is empty');
      return;
    }

    try {
      // Build shipping address
      const shippingAddress: Address = {
        line1: formData.line1,
        line2: formData.line2 || undefined,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postal_code,
        country: formData.country,
      };

      // Generate idempotency key
      const idempotencyKey = `order-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Create order request matching OpenAPI spec
      const orderRequest: CreateOrderRequest = {
        idempotency_key: idempotencyKey,
        customer_sub: user.sub,
        customer_email: user.email,
        customer_name: formData.name || user.name,
        customer_phone: formData.phone,
        cash_on_delivery: codSelected,
        shipping_address: shippingAddress,
        items: cartItems.map(item => ({
          product_id: item.productId,
          quantity: item.quantity,
        })),
      };

      const response = await mutateAsync(orderRequest);

      showSuccess('Order placed successfully!');
      clearCart();
      navigate(`/order-success/${response.order.id}`, { state: { cash_on_delivery: codSelected } });
    } catch (error: any) {
      console.error('Order creation failed:', error?.response?.data?.error?.message || error?.message);
      const errorMessage = error?.response?.data?.error?.message || error?.message || 'Failed to create order. Please try again.';
      showError(errorMessage);
      
      if (error?.response?.status === 401) {
        showError('Session expired. Please login again.');
        navigate('/login');
      } else if (error?.response?.status === 400) {
        showError('Invalid order data. Please check your information.');
      }
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6 md:py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div className="space-y-2">
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-[#111418] dark:text-white uppercase leading-none">Checkout</h1>
          <p className="text-gray-500 font-medium tracking-tight">Finalize your premium selection and secure your delivery.</p>
        </div>
        <Link to="/cart" className="text-sm font-bold text-primary flex items-center gap-2 hover:gap-3 transition-all mb-1">
          <ArrowLeft className="size-4" /> Return to Bag
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
        <div className="lg:col-span-8 space-y-12">
          {/* Section 1: Shipping */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="size-8 bg-[#111418] dark:bg-white text-white dark:text-black flex items-center justify-center rounded-full font-black text-xs">1</div>
              <h2 className="text-xl font-black tracking-tight text-[#111418] dark:text-white uppercase">Shipping Information</h2>
              {isAddressesLoading && <Loader2 className="size-4 animate-spin text-primary" />}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1">Full Name *</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  maxLength={255}
                  className={`h-12 rounded-2xl border ${formErrors.name ? 'border-red-500 focus-visible:ring-red-500' : 'border-border/40'}`}
                  placeholder="John Doe"
                />
                {formErrors.name && <p className="text-xs text-red-500 font-bold px-1">Required</p>}
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1">Phone *</label>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  maxLength={32}
                  className={`h-12 rounded-2xl border ${formErrors.phone ? 'border-red-500 focus-visible:ring-red-500' : 'border-border/40'}`}
                  placeholder="+1-555-0123"
                />
                {formErrors.phone && <p className="text-xs text-red-500 font-bold px-1">Required</p>}
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1">Address Line 1 *</label>
                <Input
                  name="line1"
                  value={formData.line1}
                  onChange={handleInputChange}
                  className={`h-12 rounded-2xl border ${formErrors.line1 ? 'border-red-500 focus-visible:ring-red-500' : 'border-border/40'}`}
                  placeholder="123 Main St"
                />

                {formErrors.line1 && <p className="text-xs text-red-500 font-bold px-1">Required</p>}
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1">Address Line 2</label>
                <Input
                  name="line2"
                  value={formData.line2}
                  onChange={handleInputChange}
                  className="h-12 rounded-2xl border border-border/40"
                  placeholder="Apt, Suite, Unit (optional)"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1">City *</label>
                <Input
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  maxLength={128}
                  className={`h-12 rounded-2xl border ${formErrors.city ? 'border-red-500 focus-visible:ring-red-500' : 'border-border/40'}`}
                  placeholder="San Francisco"
                />
                {formErrors.city && <p className="text-xs text-red-500 font-bold px-1">Required</p>}
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1">State *</label>
                <Input
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  maxLength={128}
                  className={`h-12 rounded-2xl border ${formErrors.state ? 'border-red-500 focus-visible:ring-red-500' : 'border-border/40'}`}
                  placeholder="CA"
                />
                {formErrors.state && <p className="text-xs text-red-500 font-bold px-1">Required</p>}
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1">Postal Code *</label>
                <Input
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleInputChange}
                  maxLength={32}
                  className={`h-12 rounded-2xl border ${formErrors.postal_code ? 'border-red-500 focus-visible:ring-red-500' : 'border-border/40'}`}
                  placeholder="94102"
                />
                {formErrors.postal_code && <p className="text-xs text-red-500 font-bold px-1">Required</p>}
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1">Country *</label>
                <Input
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  maxLength={2}
                  className={`h-12 rounded-2xl border ${formErrors.country ? 'border-red-500 focus-visible:ring-red-500' : 'border-border/40'}`}
                  placeholder="US"
                />
                {formErrors.country && <p className="text-xs text-red-500 font-bold px-1">Required</p>}
              </div>
            </div>
          </section>

          <Separator className="opacity-50" />

           {/* Section 2: Delivery Option */}
           <section className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="size-8 bg-[#111418] dark:bg-white text-white dark:text-black flex items-center justify-center rounded-full font-black text-xs">2</div>
                <h2 className="text-xl font-black tracking-tight text-[#111418] dark:text-white uppercase">Delivery Option</h2>
              </div>
              <button
                type="button"
                onClick={() => setCodSelected(prev => !prev)}
                className={`w-full p-6 bg-gray-50 dark:bg-gray-900 rounded-[2rem] border-2 flex items-center gap-4 cursor-pointer text-left transition-colors ${codSelected ? 'border-primary' : 'border-border/40'}`}
              >
                <div className={`size-12 rounded-[1.2rem] flex items-center justify-center shrink-0 transition-colors ${codSelected ? 'bg-primary/10' : 'bg-gray-200 dark:bg-gray-800'}`}>
                  <Banknote className={`size-6 transition-colors ${codSelected ? 'text-primary' : 'text-gray-400'}`} />
                </div>
                <div className="flex-1 space-y-0.5">
                  <p className="font-black text-[#111418] dark:text-white tracking-tight">Cash on Delivery</p>
                  <p className="text-xs text-gray-500 font-medium">Pay when your order is delivered to your doorstep.</p>
                </div>
                <div
                  className={`w-11 h-6 rounded-full p-0.5 transition-colors shrink-0 ${codSelected ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                >
                  <div className={`size-5 rounded-full bg-white shadow-sm transition-transform ${codSelected ? 'translate-x-5' : 'translate-x-0'}`} />
                </div>
              </button>
            </section>
        </div>

        {/* Sidebar Summary */}
        <div className="lg:col-span-4 relative z-20">
          <div className="bg-[#111418] text-white rounded-[2.5rem] p-8 md:p-10 space-y-8 lg:sticky lg:top-24 shadow-2xl">
            <h2 className="text-2xl font-black tracking-tight uppercase">Order Details</h2>

            {isCalculating ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="size-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-4">
                {calculationFailed && (
                  <p className="text-[10px] font-bold text-yellow-400 uppercase tracking-widest">Estimated totals — could not reach pricing service</p>
                )}
                <div className="flex justify-between items-center text-sm font-bold">
                  <span className="text-gray-400">Items ({cartItems.length})</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm font-bold">
                  <span className="text-gray-400">Shipping</span>
                  <span>${shipping.toFixed(2)}</span>
                </div>
                {tax > 0 && (
                  <div className="flex justify-between items-center text-sm font-bold">
                    <span className="text-gray-400">Tax</span>
                    <span>${tax.toFixed(2)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between items-center text-sm font-bold text-green-400">
                    <span>Discount</span>
                    <span>-${discount.toFixed(2)}</span>
                  </div>
                )}
                <Separator className="bg-white/10" />
                <div className="flex justify-between items-center">
                  <span className="text-lg font-black uppercase">Total</span>
                  <span className="text-2xl font-black text-primary">${total.toFixed(2)}</span>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={handleConfirmOrder}
              disabled={isLoading || cartItems.length === 0}
              className="w-full h-16 rounded-2xl text-base font-black shadow-2xl shadow-primary/20 bg-primary hover:bg-primary/90 active:scale-[0.98] active:shadow-inner text-white flex items-center justify-center gap-2 border-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-transform"
            >
              {isLoading ? (
                <>
                  <Loader2 className="size-5 animate-spin" /> Processing...
                </>
              ) : (
                <>
                  Confirm Order <ShieldCheck className="size-5" />
                </>
              )}
            </button>


          </div>
        </div>
      </div>
    </div>
  );
};
